import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import moment from "moment";
import { useSnackbar } from "notistack";
import { getAllFormsList_v1 } from "src/DAL/Forms/forms";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrIcon from "src/design-system/components/GrIcon";
import GrIconButton from "src/design-system/components/GrIconButton";
import GrButton from "src/design-system/components/GrButton";
import GrCard from "src/design-system/components/GrCard";
import GrPageHeader from "src/design-system/components/GrPageHeader";
import GrPage from "src/design-system/components/GrPage";
import GrTabs from "src/design-system/components/GrTabs";
import GrSearch from "src/design-system/components/GrSearch";
import GrDataTable from "src/design-system/components/GrDataTable";
import GrIconTile from "src/design-system/components/GrIconTile";
import GrStatusPill from "src/design-system/components/GrStatusPill";
import GrEmptyState from "src/design-system/components/GrEmptyState";
import GrLoader from "src/design-system/components/GrLoader";
import GrText from "src/design-system/components/GrText";
import { GrMenu, GrMenuItem } from "src/design-system/components/GrMenu";

/**
 * Your Information (/forms): forms the gym or coach sent this member.
 * Filter by status, search by title, open a form to fill it in or view it.
 */

const PAGE_SIZE = 20;
const SEARCH_DELAY_MS = 350;
// Same key and shape as before, so a saved search / status still applies.
const SAVED_FILTER_KEY = "UserFormSListSearch";
// Status tabs; "all" stands for no status filter (saved as "").
const STATUS_TABS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "submitted", label: "Submitted" },
];

const readSavedFilter = () => {
  try {
    return JSON.parse(localStorage.getItem(SAVED_FILTER_KEY)) || {};
  } catch (_e) {
    return {};
  }
};

const isSubmitted = (form) => form.submission_status === "submitted";

const formDate = (date) => (date ? moment(date).format("D MMM YYYY") : "—");
const submittedAt = (form) => (isSubmitted(form) ? form.answers?.[0]?.createdAt : null);

// Same toolbar as Chat › Friends: tabs on the left, search on the right, one divider under both;
// on phones the search goes above the tabs.
const toolbarSx = {
  display: "flex",
  alignItems: "center",
  gap: gr.space[4],
  mb: gr.space[4],
  borderBottom: `1px solid ${gr.color.line}`,
  "@media (max-width: 599.95px)": {
    flexDirection: "column-reverse",
    alignItems: "stretch",
  },
};
const searchSx = { width: { xs: "100%", sm: 260 }, flex: "none" };
const tableSx = (busy) => ({ opacity: busy ? 0.6 : 1, transition: "opacity .15s ease" });
const formCellSx = { display: "flex", alignItems: "center", gap: gr.space[3], minWidth: 0 };
const formTitleSx = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: gr.space[2],
  minWidth: 0,
  fontWeight: gr.font.weight.semibold,
  overflowWrap: "anywhere",
};
const statusCellSx = { display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: "2px" };
const footerSx = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
  gap: gr.space[3],
  mt: gr.space[4],
  pt: gr.space[4],
  borderTop: `1px solid ${gr.color.line2}`,
};

export default function UserForms() {
  const navigate = useNavigate();
  const { userInfo } = useContentSetting();
  const { enqueueSnackbar } = useSnackbar();
  const [saved] = useState(readSavedFilter);
  const [status, setStatus] = useState(saved.submission_status || "");
  const [searchText, setSearchText] = useState(saved.search || "");
  const [search, setSearch] = useState((saved.search || "").trim());
  const [forms, setForms] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loadState, setLoadState] = useState("loading"); // loading (first time) | ready | error
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [menu, setMenu] = useState(null); // { anchor, form }
  const [reloadKey, setReloadKey] = useState(0);
  const requestRef = useRef(0);

  // Search runs a moment after typing stops.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchText.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    localStorage.setItem(
      SAVED_FILTER_KEY,
      JSON.stringify({ search, submission_status: status })
    );
  }, [search, status]);

  // Only the newest request may update the list.
  const fetchPage = useCallback(
    async (nextPage) => {
      const requestId = ++requestRef.current;
      try {
        // Only forms the gym has active; inactive ones stay hidden (counts and pages follow).
        const result = await getAllFormsList_v1(nextPage, PAGE_SIZE, {
          status: "active",
          submission_status: status,
          search,
        });
        return requestId === requestRef.current ? result : null;
      } catch (_e) {
        return requestId === requestRef.current
          ? { code: 500, message: "Could not load your forms" }
          : null;
      }
    },
    [status, search]
  );

  // Filter / search change: keep the current rows on screen until the new ones arrive.
  useEffect(() => {
    let alive = true;
    setRefreshing(true);
    fetchPage(0).then((result) => {
      if (!alive) return;
      // Always end the refresh (a newer request may have replaced this one's result).
      if (!result) {
        setRefreshing(false);
        return;
      }
      if (result.code === 200) {
        setForms(result.forms || []);
        setPage(0);
        setTotal(result.total_forms || 0);
        setTotalPages(result.total_pages || 0);
        setLoadState("ready");
      } else {
        enqueueSnackbar(result.message, { variant: "error" });
        setLoadState((state) => (state === "loading" ? "error" : state));
      }
      setRefreshing(false);
    });
    return () => {
      alive = false;
    };
  }, [fetchPage, enqueueSnackbar, reloadKey]);

  const loadMore = async () => {
    setLoadingMore(true);
    const result = await fetchPage(page + 1);
    if (result?.code === 200) {
      setForms((rows) => [...rows, ...(result.forms || [])]);
      setPage((value) => value + 1);
      setTotal(result.total_forms || 0);
      setTotalPages(result.total_pages || 0);
    } else if (result) {
      enqueueSnackbar(result.message, { variant: "error" });
    }
    setLoadingMore(false);
  };

  const openForm = (form) =>
    navigate(`/forms-submition/${form.form_slug}`, { state: form });
  const previewForm = (form) =>
    navigate(`/forms-Details/${form._id}/${userInfo._id}`, { state: form });
  const runMenu = (action) => {
    const form = menu.form;
    setMenu(null);
    action(form);
  };
  const retry = () => {
    setLoadState("loading");
    setReloadKey((key) => key + 1);
  };
  const clearFilters = () => {
    setStatus("");
    setSearchText("");
    setSearch("");
  };

  const filtered = Boolean(status || search);
  const hasMore = page < totalPages;

  return (
    <GrPage>
      <GrPageHeader
        title="Your Information"
        subtitle="Forms from your gym and your coach. Fill in the pending ones."
      />

      <Box sx={toolbarSx}>
        <GrTabs
          ariaLabel="Show forms"
          value={status || "all"}
          onChange={(_e, value) => setStatus(value === "all" ? "" : value)}
          tabs={STATUS_TABS.map((tab) => ({
            ...tab,
            id: `forms-tab-${tab.value}`,
            "aria-controls": "forms-tabpanel",
          }))}
          sx={{ flex: 1, minWidth: 0, borderBottom: 0 }}
        />
        <GrSearch
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onClear={() => setSearchText("")}
          placeholder="Search forms…"
          label="Search forms"
          sx={searchSx}
        />
      </Box>

      <GrCard role="tabpanel" id="forms-tabpanel" aria-labelledby={`forms-tab-${status || "all"}`}>
        {loadState === "loading" ? (
          <GrLoader label="Loading your forms" />
        ) : loadState === "error" ? (
          <GrEmptyState
            compact
            icon={<GrIcon icon={icons.file} />}
            title="Your forms could not be loaded."
            action={
              <GrButton variant="secondary" onClick={retry}>
                Try again
              </GrButton>
            }
          />
        ) : forms.length === 0 ? (
          <GrEmptyState
            icon={<GrIcon icon={icons.file} />}
            title={filtered ? "No forms match" : "No forms yet"}
            text={
              filtered
                ? "Try another search or status."
                : "When your gym or coach sends you a form, it shows up here."
            }
            action={
              filtered ? (
                <GrButton variant="quiet" onClick={clearFilters}>
                  Show all forms
                </GrButton>
              ) : null
            }
          />
        ) : (
          <>
            <Box aria-busy={refreshing} sx={tableSx(refreshing)}>
              <GrDataTable
                ariaLabel="Your forms"
                rows={forms}
                getRowKey={(form) => form._id}
                columns={[
                  {
                    key: "form",
                    label: "Form",
                    primary: true,
                    width: "minmax(0, 2.2fr)",
                    render: (form) => (
                      <Box sx={formCellSx}>
                        <GrIconTile size="sm" tone={isSubmitted(form) ? "ok" : "brand"}>
                          <GrIcon icon={icons.file} />
                        </GrIconTile>
                        <Box component="span" sx={formTitleSx}>
                          {form.title}
                        </Box>
                      </Box>
                    ),
                  },
                  {
                    key: "sent",
                    label: "Date sent",
                    render: (form) => formDate(form.createdAt),
                  },
                  {
                    key: "status",
                    label: "Status",
                    render: (form) => (
                      <Box component="span" sx={statusCellSx}>
                        <GrStatusPill status={isSubmitted(form) ? "ok" : "due"}>
                          {isSubmitted(form) ? "Submitted" : "Pending"}
                        </GrStatusPill>
                        {submittedAt(form) ? (
                          <GrText as="span" variant="caption">
                            on {formDate(submittedAt(form))}
                          </GrText>
                        ) : null}
                      </Box>
                    ),
                  },
                  {
                    key: "actions",
                    label: "Actions",
                    action: true,
                    render: (form) => (
                      <GrIconButton
                        size="sm"
                        aria-label={`Actions for ${form.title}`}
                        aria-haspopup="menu"
                        onClick={(e) => setMenu({ anchor: e.currentTarget, form })}
                      >
                        <GrIcon icon={icons.more} />
                      </GrIconButton>
                    ),
                  },
                ]}
              />
            </Box>

            <Box sx={footerSx}>
              <GrText variant="caption">
                Showing {forms.length} of {total} form{total === 1 ? "" : "s"}
              </GrText>
              {hasMore ? (
                <GrButton
                  variant="secondary"
                  size="sm"
                  loading={loadingMore}
                  // Not while a filter / search refresh runs: its rows would mix with the old ones.
                  disabled={refreshing}
                  onClick={loadMore}
                >
                  Load more
                </GrButton>
              ) : null}
            </Box>
          </>
        )}
      </GrCard>

      <GrMenu
        anchorEl={menu?.anchor || null}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {menu && !isSubmitted(menu.form) ? (
          <GrMenuItem onClick={() => runMenu(openForm)}>
            <GrIcon icon={icons.edit} />
            Fill in
          </GrMenuItem>
        ) : null}
        <GrMenuItem onClick={() => runMenu(previewForm)}>
          <GrIcon icon={icons.eye} />
          {menu && isSubmitted(menu.form) ? "View answers" : "Preview"}
        </GrMenuItem>
      </GrMenu>
    </GrPage>
  );
}
